import * as React from "react";
import {
    Box,
    Grid,
    Backdrop,
    CircularProgress,
    Button,
    Typography,
    useTheme,
    Divider,
    Tab,
    Tabs,
} from "@mui/material";
import { useState, useEffect } from "react";
import { tokens } from "../../theme";
import Header from "../../components/Header";
import InspectionType from "./InspectionType";
import axiosClient from "../../axios";
import InspectionDetails from "./InspectionDetails";
import { set } from "lodash";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import prettyDate from "../../formatdate";
import { debounce } from "lodash";
import { el } from "date-fns/locale";

// Same order as the non-admin tabs; `key` is passed to InspectionDetails as `tab`
const adminTabs = [
    { key: "incomplete", label: "Incomplete Requirements", api_path: "/inspections/all_active_inspections" },
    { key: "overdue", label: "Overdue Requirements", api_path: "/inspections/overdue_inspections" },
    { key: "escalated", label: "Escalated Requirements", api_path: null }, // Date range built on fetch
    { key: "submitted", label: "Submitted Requirements", api_path: "/inspections/all_completed_submissions" },
    { key: "completed", label: "Completed Requirements", api_path: "/inspections/all_completed_inspections" },
    { key: "archived", label: "Archived Surveys", api_path: "/inspections/all_archived_inspections" },
    { key: "fetched", label: "Fetched Surveys", api_path: "/inspections/get_all_new_inspections" },
];

export default function AllInspections() {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [currentTabIndex, setCurrentTabIndex] = useState(0);
    const [inspections, setInspections] = useState([]); // Send this to InspectionType (Parent to Child)
    const [selectedInspection, setSelectedInspection] = useState(null); // Get this from InspectionType (Child to Parent)
    const [displayInspectionID, setDisplayInspectionID] = useState(null); // Send this to InspectionDetails (Parent to Child)
    const [backdropOpen, setBackdropOpen] = useState(false);

    const handleTabChange = (e, tabIndex) => {
        setInspections([]);
        setSelectedInspection(null);
        setDisplayInspectionID(null);
        setCurrentTabIndex(tabIndex);
    };

    useEffect(
        debounce(() => {
            setBackdropOpen(true);
            async function getInspections() {
                const currentTab = adminTabs[currentTabIndex];
                let api_path = currentTab.api_path;
                if (currentTab.key === "escalated") {
                    const tomorrow = new Date(new Date().setDate(new Date().getDate() + 1)).toISOString().split('T')[0];
                    api_path = "/inspections/escalated_inspections_dates/2023-01-01/" + tomorrow;
                }
                const response = await axiosClient.get(api_path);
                // Loop through object of response.data and create a new array on inspections
                const loaded_inspections = [];
                for (const key in response.data) {
                    // Store in loaded_inspections
                    loaded_inspections.push({
                        id: response.data[key].id,
                        selectItem:
                            response.data[key].audit_policy_holder +
                            " - " +
                            response.data[key].audit_broker_name +
                            " - " +
                            prettyDate(response.data[key].audit_survey_date),
                    });
                }
                setInspections(loaded_inspections);
                setBackdropOpen(false);
                // console.log("Loaded Inspections: ", loaded_inspections);
            }
            getInspections();
        }, 1000), // Debounce delay of 1000 milliseconds
        [currentTabIndex] // On change of currentTabIndex, run useEffect
    );

    // Get Inspection ID from InspectionType (Child to Parent)
    const getSelectedInspection = (inspectionDetails) => {
        setSelectedInspection(inspectionDetails);
        setDisplayInspectionID(inspectionDetails.id);
    };

    return (
        <Box m="20px">
            <Header
                title="INSPECTIONS"
                subtitle="Search for survey details by selecting appropriate tabs below."
            />
            <Card
                variant="outlined"
                sx={{
                    marginTop: "40px",
                    borderTop: 3,
                    borderTopColor: colors.greenAccent[400],
                    borderRadius: 0,
                }}
            >
                <CardContent>
                    <Tabs
                        value={currentTabIndex}
                        onChange={handleTabChange}
                        indicatorColor="secondary"
                        textColor="secondary"
                    >
                        {adminTabs.map(({ key, label }) => (
                            <Tab key={key} label={label} />
                        ))}
                    </Tabs>
                    {/* Tab contents - InspectionDetails decides which actions to show from `tab` */}
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12}>
                            <InspectionType
                                inspections={inspections}
                                getSelectedInspection={getSelectedInspection}
                            />
                        </Grid>
                    </Grid>
                    {currentTabIndex === 0 && <Divider />}
                    <Box paddingBottom={currentTabIndex === 0 ? 2 : 0} sx={{ flexGrow: 1 }}>
                        <InspectionDetails
                            key={adminTabs[currentTabIndex].key}
                            inspectionID={displayInspectionID}
                            tab={adminTabs[currentTabIndex].key}
                        />
                    </Box>

                    {/* Render Backdrop */}
                    <Backdrop
                        sx={{
                            color: "#fff",
                            zIndex: (theme) => theme.zIndex.drawer + 1,
                        }}
                        open={backdropOpen}
                    >
                        <CircularProgress color="inherit" />
                    </Backdrop>
                </CardContent>
            </Card>
        </Box>
    );
}
